// Planning-grade tidal zoning estimate: NOT a NOS tide note. Real discrete tidal zoning draws
// zone boundaries from co-tidal lines and project-specific control stations (see
// docs/research/tide-tool.md section 5). This is a much simpler straight-line stand-in for early
// project scoping, before a certified tide note exists: pick two bracketing CO-OPS stations, take
// whichever is nearer as the reference curve, and linearly interpolate a time corrector and a
// range ratio between the two stations based on where the project point falls on the line between
// them. Always label output "planning estimate" in the UI.

function meanRange(hilo) {
  const sorted = [...hilo].sort((a, b) => a.t - b.t);
  const diffs = [];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].type !== sorted[i - 1].type) diffs.push(Math.abs(sorted[i].v - sorted[i - 1].v));
  }
  if (!diffs.length) return null;
  return diffs.reduce((a, b) => a + b, 0) / diffs.length;
}

// Average lag (hours, B minus A) between each high tide in A and the nearest high tide in B.
function meanHighTideLagHours(hiloA, hiloB) {
  const highsA = hiloA.filter((p) => p.type === "H").sort((a, b) => a.t - b.t);
  const highsB = hiloB.filter((p) => p.type === "H").sort((a, b) => a.t - b.t);
  if (!highsA.length || !highsB.length) return 0;
  const lags = [];
  for (const a of highsA) {
    let best = null;
    let bestAbs = Infinity;
    for (const b of highsB) {
      const diffH = (b.t - a.t) / 3_600_000;
      if (Math.abs(diffH) < bestAbs) { bestAbs = Math.abs(diffH); best = diffH; }
    }
    if (best != null && bestAbs < 18) lags.push(best);
  }
  if (!lags.length) return 0;
  return lags.reduce((a, b) => a + b, 0) / lags.length;
}

// Fraction (0..1) of the project point's position projected onto the line from A to B, in plain
// lon/lat degrees -- fine for the short, local distances this is meant for; not geodesically exact.
function projectionFraction(point, a, b) {
  const abx = b.lon - a.lon, aby = b.lat - a.lat;
  const apx = point.lon - a.lon, apy = point.lat - a.lat;
  const ab2 = abx * abx + aby * aby;
  if (ab2 === 0) return 0;
  return Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));
}

// stationA/B: { lon, lat, mtl, hilo: [{t,v,type}] }. point: { lon, lat }.
// Returns the corrected hi/lo series at `point`, plus the correctors applied, or null if either
// station has no usable hi/lo data.
export function estimateZoneCorrection(stationA, stationB, point) {
  const rangeA = meanRange(stationA.hilo || []);
  const rangeB = meanRange(stationB.hilo || []);
  if (!Number.isFinite(rangeA) || !Number.isFinite(rangeB)) return null;

  const distA = Math.hypot(point.lon - stationA.lon, point.lat - stationA.lat);
  const distB = Math.hypot(point.lon - stationB.lon, point.lat - stationB.lat);
  const reference = distA <= distB ? stationA : stationB;
  const other = distA <= distB ? stationB : stationA;
  const rangeRef = distA <= distB ? rangeA : rangeB;
  const rangeOther = distA <= distB ? rangeB : rangeA;

  const w = projectionFraction(point, stationA, stationB); // 0 at A, 1 at B
  const wTowardOther = distA <= distB ? w : 1 - w;

  const lagAtoB = meanHighTideLagHours(stationA.hilo, stationB.hilo);
  const lagRefToOther = distA <= distB ? lagAtoB : -lagAtoB;
  const timeCorrectorHours = wTowardOther * lagRefToOther;
  const rangeRatio = 1 + wTowardOther * (rangeOther / rangeRef - 1);

  const mtl = Number.isFinite(reference.mtl) ? reference.mtl : 0;
  const corrected = (reference.hilo || []).map((p) => ({
    t: p.t + timeCorrectorHours * 3_600_000,
    v: mtl + (p.v - mtl) * rangeRatio,
    type: p.type,
  }));

  return {
    referenceStationId: reference.id,
    otherStationId: other.id,
    weightTowardOther: wTowardOther,
    timeCorrectorHours,
    rangeRatio,
    rangeReference: rangeRef,
    rangeOther,
    corrected,
  };
}
